import { FormRow } from 'framework/ui/layout';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { useFormShowContext, InputList, EnumDropdown } from 'framework/form';
import { createTruncatedTextFormat, FormattedTextField, getAnchorRowStyle } from 'framework/ui/field';
import { defaultsDeep } from 'lodash';
import { Alert, Tooltip } from 'framework/ui/mui-builder';
import TeamPositionsInputs from './input/TeamPositionsInputs';
import {
  EditableListDataGridLayout,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
} from 'framework/list';
import { useShowContext } from 'framework/show';
import { PROJECT_TEAM_LOCATION_LABELS } from './constants';
import { MapedExpectedStructure, TeamShowType } from './types';
import ProjectTeamSlotPositionField from './field/ProjectTeamSlotPositionField';
import FTEBadge from './field/FTEBadge';
import ProjectTeamIssuesField from './field/ProjectTeamIssuesField';
import TeamMemberField from './field/TeamMemberField';
import ProjectTeamMemberPositionField from './field/ProjectTeamMemberPositionField';
import ProjectTeamMemberRoleField from './field/ProjectTeamMemberRoleField';
import AssignMemberTimeField from './field/AssignMemberTimeField';
import { useProjectSecurityContext } from '../context/ProjectSecurityContext';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS } from '../constants';
import { EmployeeDisplayDataProvider } from 'domain/shared/employee';

const ProjectTeamForm = () => {
  const { isEditMode } = useFormShowContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { data } = useShowContext<TeamShowType>();
  const isFullAccess = isAccessGranted(ACCESS_INFO_SECTIONS.TEAM, ACCESS_INFO_ACTIONS.MANAGE);
  const members = data?.getData?.()?.team?.members || [];
  const employeeAliases = Array.from(new Set(members.map(({ employeeAlias }) => employeeAlias).filter(Boolean)));

  return (
    <EmployeeDisplayDataProvider aliases={employeeAliases}>
      <Box minWidth="1000px">
        {isEditMode ? (
          <Box padding={1} px={2}>
            <Alert severity="warning">
              Changing the team structure may affect current assignments. Staffing coordinators will be notified of the
              updates.
            </Alert>
            <Box display="flex" pt={1}>
              <FormRow>
                <EnumDropdown label="Team location" source="team.location" options={PROJECT_TEAM_LOCATION_LABELS} />
              </FormRow>
              <Box width="26px" ml={2.5} />
            </Box>
            <InputList
              inputTemplate={TeamPositionsInputs}
              source="team.expectedStructure"
              addText="Add position"
              disabledRemove={(slotData: MapedExpectedStructure) => !!slotData?.members?.length}
              removeButtonTooltipe={(slotData: MapedExpectedStructure) =>
                !!slotData?.members?.length ? 'Slot is not empty.' : null
              }
              maxFields={30}
              defaultValue={{}}
            />
          </Box>
        ) : (
          <EditableListDataGridLayout
            rowProps={rowData =>
              defaultsDeep(getAnchorRowStyle(rowData, ''), {
                sx: {
                  pb: 0,
                },
              })
            }
            getRowEnabled={() => true}
            getTableCells={() =>
              Promise.resolve([
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Grid,
                  componentProps: {
                    container: true,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    columnSpacing: 1,
                    columns: 13,
                  },
                  gridCellProps: {
                    size: 13,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'positionId',
                      isEditor: false,
                      component: ProjectTeamSlotPositionField,
                      gridCellProps: {
                        size: 2.7,
                      },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'requiredFte',
                      isEditor: false,
                      component: FTEBadge,
                      componentProps: {
                        assignedFteSource: 'assignedFte',
                      },
                      gridCellProps: {
                        size: isFullAccess ? 6.3 : 8.3,
                      },
                    },
                    ...(isFullAccess
                      ? [
                          {
                            type: EDITABLE_LIST_FIELD,
                            source: 'description',
                            isEditor: false,
                            component: FormattedTextField,
                            componentProps: {
                              format: createTruncatedTextFormat(40),
                            },
                            gridCellProps: {
                              size: 2,
                            },
                          },
                        ]
                      : []),
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'issues',
                      isEditor: false,
                      component: ProjectTeamIssuesField,
                      componentProps: {},
                      gridCellProps: {
                        size: 2,
                      },
                    },
                  ],
                },

                {
                  type: EDITABLE_LIST_GROUP,
                  component: Grid,
                  componentProps: {
                    container: true,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: 1,
                    borderBottom: '1px solid var(--mui-palette-TableCell-border)',
                  },
                  gridCellProps: {
                    size: 12,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_NESTED_LIST,
                      source: 'members',
                      gridCellProps: { size: 12 },
                      isEditor: true,
                      component: Grid,
                      rows: {
                        emptyListComponent: (
                          <Typography variant="body2" color="textSecondary">
                            No active assignments.
                          </Typography>
                        ),
                      },
                      componentProps: {
                        direction: 'row',
                        container: true,
                        columnSpacing: 1,
                        columns: 13,
                      },
                      children: [
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'employeeAlias',
                          isEditor: false,
                          component: TeamMemberField,
                          gridCellProps: {
                            size: 2.7,
                            pl: 2.5,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'fte',
                          isEditor: false,
                          component: FTEBadge,
                          componentProps: {
                            isMemberFter: true,
                            minPercentSource: 'minPercent',
                            maxPercentSource: 'maxPercent',
                          },
                          gridCellProps: {
                            size: 0.8,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'positionId',
                          isEditor: false,
                          component: ProjectTeamMemberPositionField,
                          componentProps: {
                            positionIdSource: `positionId`,
                            technologyIdSource: `technologyId`,
                            competencyLevelSource: `competencyLevel`,
                          },
                          gridCellProps: {
                            size: 2,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'role',
                          isEditor: false,
                          component: ProjectTeamMemberRoleField,
                          componentProps: {},
                          gridCellProps: {
                            size: 1.5,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'startDate',
                          isEditor: false,
                          component: AssignMemberTimeField,
                          componentProps: {
                            dateFromSource: 'startDate',
                            dateToSource: 'endDate',
                          },
                          gridCellProps: {
                            size: 2,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'noteText',
                          isEditor: false,
                          component: FormattedTextField,
                          componentProps: {
                            format: (str: string) =>
                              str && str.length > 40 ? (
                                <Tooltip title={str}>
                                  <div>
                                    <Typography
                                      sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 1,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        lineHeight: 1.2,
                                        wordBreak: 'break-word',
                                      }}
                                    >
                                      {str}
                                    </Typography>
                                  </div>
                                </Tooltip>
                              ) : (
                                str
                              ),
                          },
                          gridCellProps: {
                            size: 2,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'issues',
                          isEditor: false,
                          component: ProjectTeamIssuesField,
                          componentProps: {},
                          gridCellProps: {
                            size: 2,
                          },
                        },
                      ],
                    },
                  ],
                },
              ])
            }
          />
        )}
      </Box>
    </EmployeeDisplayDataProvider>
  );
};
export default ProjectTeamForm;
